import React, {useState} from "react";
import {useTranslation} from "react-i18next";
import {useDispatch} from "useDispatch";
import {useForm} from "react-hook-form";
import {yupResolver} from "@hookform/resolvers/yup";
import * as yup from "yup";
import BookingAction from "store/booking/BookingAction";
import BookingLoginInput from "pages/Front/Booking/components/BookingLoginInput";
import {FieldErrors} from "react-hook-form/dist/types/errors";
import {consoleLog, REGEX_EMAIL} from "utils/utility";
import BookingLoginErrors from "pages/Front/Booking/BookingLogin/components/BookingLoginErrors";
import BookingLoginZuumeHeader from "pages/Front/Booking/BookingLogin/components/BookingLoginReservationDetail/components/BookingLoginZuumeHeader";
import CardComponentBlackHeader from "pages/Front/Booking/components/CardComponentBlackHeader";
import BookingLoginReservationButton from "pages/Front/Booking/BookingLogin/components/BookingLoginReservationDetail/components/BookingLoginReservationButton";
import ReservationButtonInner from "pages/Front/Booking/BookingLogin/components/BookingLoginReservationDetail/components/ReservationButtonInner";
import LoadingIcon from "components/Icons/LoadingIcon";
import FeatherIcon from "components/FeatherIcon";

const BookingLoginEmail = (props: {setSecurityCodeSent: (value: boolean) => void; setEmail: (email: string) => void}) => {
    const {t} = useTranslation();
    const dispatch = useDispatch();
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [securityCodeSent, setSecurityCodeSent] = useState<boolean>(false);
    const LoginSchema = yup.object().shape({
        email: yup
            .string()
            .required("Email is required field.")
            .test("email", "Invalid email format", value => {
                return REGEX_EMAIL.test(value);
            }),
    });

    const defaultValuesLogin = {
        email: "",
    };
    const {
        register,
        handleSubmit,
        setError,
        formState: {errors},
    } = useForm({
        resolver: yupResolver<any>(LoginSchema),
        defaultValues: defaultValuesLogin,
    });

    const onSubmitError = (data: FieldErrors) => {
        consoleLog("onSubmitError", data);
    };

    const onSubmitLogin = (data: {email: string}) => {
        props.setEmail(data.email);
        consoleLog("onSubmitLogin", data);
        setSecurityCodeSent(false);

        if (isSubmitting) {
            return;
        }
        setIsSubmitting(true);

        dispatch(BookingAction.fetchSendSecurityCodeForLogin(data.email))
            .then(
                onResolve => {
                    consoleLog("onSubmitLogin - onResolve", onResolve);
                    props.setSecurityCodeSent(true);
                },
                onReject => {
                    consoleLog("onSubmitLogin - onReject", onReject);

                    const emailErrorMsg =
                        onReject === "not_found"
                            ? t("Your security code is incorrect.")
                            : onReject === "login_not_found"
                              ? t("Account not found. Your email address or phone number is incorrect.")
                              : t("You must enter an email address or phone number.");

                    setError("email", {
                        type: "manual",
                        message: t(emailErrorMsg),
                    });
                }
            )
            .finally(() => {
                setIsSubmitting(false);
            });
    };

    return (
        <div className={"w-full flex flex-col justify-center items-center space-y-8"}>
            <BookingLoginZuumeHeader title={t("Login")} />
            <CardComponentBlackHeader className={"w-full max-w-md pb-24"} title={t("Login into your reservation")}>
                <form
                    autoComplete={"off"}
                    className={"LoginForm w-full max-w-xs flex flex-col items-center justify-center"}
                    onSubmit={handleSubmit(onSubmitLogin, onSubmitError)}
                >
                    <div className={"my-8 text-center text-xs md:text-sm font-normal"}>
                        <span className={"text-black"}>{t("Please enter the booking email address")}</span>
                    </div>
                    <BookingLoginInput
                        className={"w-full"}
                        errors={errors}
                        label={t("E-mail")}
                        name={"email"}
                        placeholder={"mail@example.com"}
                        register={register}
                        required={true}
                        showIcon={false}
                        type={"email"}
                    />

                    <BookingLoginReservationButton
                        className={"w-full mx-auto mt-6"}
                        disabled={isSubmitting || securityCodeSent}
                        onClick={handleSubmit(onSubmitLogin, onSubmitError)}
                    >
                        <ReservationButtonInner>
                            <span>{t("Login")}</span>
                            {isSubmitting ? (
                                <LoadingIcon className={"stroke-2 size-4"} />
                            ) : (
                                <FeatherIcon name={"arrow-right"} strokeWidth={2} className={"stroke-2 size-4"} />
                            )}
                        </ReservationButtonInner>
                    </BookingLoginReservationButton>
                    {errors && <BookingLoginErrors errors={errors} />}
                </form>
            </CardComponentBlackHeader>
        </div>
    );
};

export default BookingLoginEmail;
